import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { type ApiError, isApiError } from './ApiError';

/**
 * Per-query flags the cache-level handlers read. `handlesUnauthorized`: this query decides what a 401 means itself
 * (the session query: no session yet is the normal signed-out state, not an expiry), so skip `onUnauthorized`.
 */
export type ApiQueryMeta = { handlesUnauthorized?: boolean };

declare module '@tanstack/react-query' {
  // TanStack's documented augmentation point for typing `meta`; it must be an interface to merge.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Register {
    queryMeta: ApiQueryMeta;
    mutationMeta: ApiQueryMeta;
  }
}

export type QueryClientHooks = {
  /** A 401 from any query or mutation: the session is gone. `error.code` is `SESSION_EXPIRED` when it timed out. */
  onUnauthorized: (error: ApiError) => void;
  /** Every other failure. Wire to Sentry; never swallow. */
  onError: (error: unknown) => void;
};

/** Retry twice on network and 5xx errors; never retry a 4xx, since the request itself is wrong. */
export const shouldRetry = (failureCount: number, error: unknown): boolean =>
  failureCount < 2 && !(isApiError(error) && error.status < 500);

export const createQueryClient = ({ onUnauthorized, onError }: QueryClientHooks): QueryClient => {
  const route = (error: unknown, meta: ApiQueryMeta | undefined) => {
    if (isApiError(error) && error.status === 401) {
      if (meta?.handlesUnauthorized !== true) {
        onUnauthorized(error);
      }
      return;
    }
    onError(error);
  };

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        route(error, query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        route(error, mutation.meta);
      },
    }),
    defaultOptions: {
      queries: { retry: shouldRetry, staleTime: 30_000, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
};
